import React from 'react'
import { Autocomplete, Dropdown } from '@carsys/form'
import useGetVatTypesOptions, { AutoCorrectFallback, IAutoCorrectMode, vatFilter } from '../MaterialVatDropdown/useGetVatTypesOptions'
import LockableInput from '@carsys/form/src/LockableInput'
import { IconButton, InputAdornment } from '@mui/material'
import LockOpen from '@mui/icons-material/LockOpen'
import Lock from '@mui/icons-material/Lock'
import { useField } from 'formik'
import { useTranslation } from 'react-i18next'
import { TextField } from '@carsys/ui'

interface IVatSelection {
  name: string
  label: string
  filter?: vatFilter
  showAll?: boolean
  autoCorrectMode: IAutoCorrectMode
  autoCorrectFallback?: AutoCorrectFallback
  multiple?: boolean
  disabled?: boolean
  validate?: () => void
  required?: boolean
  [key: string]: any
}

interface IVatProps {
  vatOptions: any[]
  handleClick: () => void
  locked: boolean
  showLock?: boolean
}

type LockableAutocompleteProps = React.ComponentProps<typeof Autocomplete>
type LockableDropdownProps = React.ComponentProps<typeof Dropdown>
interface IVatAutocomplete extends IVatProps, Omit<LockableAutocompleteProps, 'options'> {}
interface IVatDropdown extends IVatProps, Omit<LockableDropdownProps, 'options'> {}

const VatAutocomplete: React.FC<IVatAutocomplete> = ({ name, label, vatOptions, handleClick, locked, required, fullWidth, ...rest }) => (
  <Autocomplete
    name={name}
    options={vatOptions}
    isOptionEqualToValue={(option, value) => (option as { value: string }).value === value}
    renderInput={({ params, input, meta, helperText }) => (
      <LockableInput
        params={params}
        props={{
          name,
          label,
          changeHandler: handleClick,
          initialValue: locked,
          input,
          meta,
          helperText,
          required,
          multiple: true,
          fullWidth
        }}
      />
    )}
    disabled={locked}
    multiple
    {...rest}
  />
)

const VatDropdown: React.FC<IVatDropdown> = ({ name, label, vatOptions, handleClick, locked, showLock, disabled, fullWidth, ...rest }) => {
  const { t } = useTranslation()

  return (
    <Dropdown
      name={name}
      label={label}
      SelectProps={{
        IconComponent: () => (
          <InputAdornment position='start'>
            {showLock || (
              <IconButton size='small' onClick={handleClick} data-testid='lock-toggle'>
                {locked ? <Lock /> : <LockOpen />}
              </IconButton>
            )}
          </InputAdornment>
        ),
        fullWidth
      }}
      {...rest}
      disabled={locked || disabled}
      options={vatOptions || []}
      noOptionsText={t('warehouse_part.no_vattype')}
    />
  )
}

const VatSelection: React.FC<IVatSelection> = ({ filter, showAll, autoCorrectMode, autoCorrectFallback, disabled, multiple, ...rest }) => {
  const { t } = useTranslation()
  const [field, meta, helpers] = useField(rest.name)
  const [locked, setLocked] = React.useState<boolean>(!!(disabled || meta.initialValue))
  const { options, isLoading } = useGetVatTypesOptions({
    currentValue: field.value,
    initialValue: meta.initialValue,
    onChange: v => helpers.setValue(v),
    filter,
    showAll,
    autoCorrectMode,
    autoCorrectFallback,
    multiple
  })

  const handleClick = () => {
    setLocked(previousValue => disabled || !previousValue)
  }

  if (isLoading) return <TextField helperText={rest.helperText} value={t('global.loading')} disabled />

  if (multiple) return <VatAutocomplete vatOptions={options} locked={locked} handleClick={handleClick} {...rest} />

  return <VatDropdown vatOptions={options} locked={locked} handleClick={handleClick} showLock={disabled} disabled={disabled} {...rest} />
}

export default VatSelection
